@if (model) {
  <div class="tile">
    @switch (model.instrumentType) {
      @case (InstrumentType.Derivative) {
        <ng-container *ngTemplateOutlet="derivativeHeader" />
      }
      @default {
        <ng-container *ngTemplateOutlet="defaultHeader" />
      }
    }
  </div>
}

<ng-template #defaultHeader>
  <div class="row opacity-container mb-2" [class.active]="model.name">
    <div class="col-lg-7">
      <div class="d-flex align-items-center title-container">
        @if (model.iconUrl) {
          <img [src]="model.iconUrl" alt="icon" class="logo mr-2" />
        }
        <h2 class="font-bold" data-zid="default-header-headline" [title]="model.name">
          {{ model.name?.length > 45 ? (model.name | slice: 0 : 45) + '...' : model.name }}
        </h2>
        <div class="icon-container-mobile">
          <ng-container *ngTemplateOutlet="starAndBell" />
        </div>
      </div>
    </div>
    @if (model.isTypeTradable) {
      <div class="col-lg-5 text-right d-none d-lg-flex">
        <ng-container *ngTemplateOutlet="actionButtons" />
      </div>
    }
  </div>
  <div class="row d-block d-md-flex justify-content-between align-items-center opacity-container" [class.active]="model.quote">
    <div class="col-lg-4 mb-2 mb-xl-0 d-sm-flex align-items-center gap">
      <div class="zero-text h1-bold" [zeroBlink]="model.perfAbs >= 0" data-zid="quote">
        {{ model.quote | number: quoteFormat }}
      </div>
      <div class="d-flex justify-content-between align-items-center d-sm-block text-right">
        <div
          class="zero-text p-small-bold quoteindicator-variant3 text-nowrap"
          [class.text-color-success]="model.perfAbs >= 0"
          [class.text-color-error]="model.perfAbs < 0"
          zeroBlink
          [val]="model.perfAbs"
        >
          @if (model.perfAbs > 0) {
            +
          }
          {{ model.perfAbs | currency: model.currencyCode : 'symbol' : quoteFormat }}
        </div>
        <div
          class="zero-text p-small-bold quoteindicator-variant3 text-nowrap"
          [class.text-color-success]="model.perfPct >= 0"
          [class.text-color-error]="model.perfPct < 0"
          zeroBlink
          [val]="model.perfPct"
        >
          @if (model.perfAbs > 0) {
            +
          }
          {{ model.perfPct | number: '1.2-2' }} %
        </div>
      </div>
    </div>
    @if (model.isTypeTradable) {
      <div class="col-lg-8 mb-2 mb-xl-0 d-inline-block d-md-flex justify-content-end gap-half">
        @if (model?.sentiment; as sentiment) {
          <ng-template #sentimentTooltip>
            <span [innerHTML]="'instrument-detail.instrument-header.sentiment.tooltip' | transloco"></span>
          </ng-template>
          <div
            class="mb-2 mb-md-0 w-fit b-r-4 p-2 zero-surface grey-800 d-inline-flex align-items-center gap-half sentiment-container"
            [ngbTooltip]="sentimentTooltip"
            tooltipClass="zero-tooltip"
            placement="bottom"
          >
            <div class="sentiment">
              <div class="buy">
                <div [style.width]="100 - sentiment.buy + '%'"></div>
                <div class="bar zero-surface" [class.secondary-800]="sentiment.buy" [style.width]="sentiment.buy + '%'"></div>
                <div class="sentiment-label zero-text p-xsmall-med" [class.dark-green]="sentiment.buy" [class.label]="!sentiment.buy">
                  {{ sentiment.buy | number: '1.0-1' }}%
                </div>
              </div>
              <div class="sell">
                <div class="bar zero-surface" [class.tertiary-800]="sentiment.sell" [style.width]="sentiment.sell + '%'"></div>
                <div [style.width]="100 - sentiment.sell + '%'"></div>
                <div class="sentiment-label zero-text p-xsmall-med" [class.dark-red]="sentiment.sell" [class.label]="!sentiment.sell">
                  {{ sentiment.sell | number: '1.0-1' }}%
                </div>
              </div>
            </div>
          </div>
        }

        <div class="d-flex gap-half mb-2 mb-md-0">
          @if (model?.ranking; as ranking) {
            <div
              class="w-fit b-r-4 px-2 zero-surface grey-800 d-flex align-items-center gap-half rank"
              [ngbTooltip]="'instrument-detail.instrument-header.rank.tooltip' | transloco"
              tooltipClass="zero-tooltip"
              placement="bottom"
            >
              <web-design-system-icon class="icon" name="ranking" />
              <div class="zero-text p-xsmall-reg">{{ ranking.rankTotal }}</div>
              <div class="trend" [ngClass]="{ up: ranking.trend === 1, down: ranking.trend === -1, neutral: ranking.trend === 0 }"></div>
            </div>
          }
          @if (model?.marketCap) {
            <div
              class="w-fit b-r-4 px-2 py-1 zero-surface grey-800 d-flex gap-small align-items-center"
              [ngbTooltip]="'instrument-detail.instrument-header.market-cap.tooltip' | transloco"
              tooltipClass="zero-tooltip"
              placement="bottom"
            >
              <web-design-system-icon size="small" name="moneybag" />
              <div class="zero-text p-xsmall-reg text-nowrap">{{ model.marketCap | bigNumbers: '1.2-2' }} €</div>
            </div>
          }
          @if (model?.kgv) {
            <div class="w-fit b-r-4 px-2 py-1 zero-surface grey-800 d-flex gap-small align-items-center">
              <div class="zero-text p-xsmall-bold">KGV:</div>
              <div class="zero-text p-xsmall-reg">{{ model.kgv | number: '1.2-2' }}</div>
            </div>
          }
        </div>
        @if (model.instrumentType !== InstrumentType.Crypto) {
          <ng-container *ngTemplateOutlet="copyIsin" />
        } @else {
          <div class="w-fit b-r-4 px-2 py-1 zero-surface grey-800 d-flex ml-0 ml-sm-2 gap-small align-items-center">
            <div class="zero-text p-xsmall-bold">Kürzel:</div>
            <div>
              <web-design-system-copy-to-clipboard
                [label]="model.shortName"
                [hint]="'instrument-detail.instrument-header.copy-shortName' | transloco"
                iconCssClass="icon-secondary"
              />
            </div>
          </div>
        }
      </div>
    }
  </div>
  @if (model.isTypeTradable) {
    <div class="row d-block d-lg-none mt-2">
      <div class="col d-flex justify-content-between">
        <ng-container *ngTemplateOutlet="actionButtons" />
      </div>
    </div>
  }
</ng-template>

<ng-template #derivativeHeader>
  <div class="opacity-container" [class.active]="model.name">
    <div class="d-flex justify-content-between align-items-start align-items-md-center mb-md-3">
      <div class="zero-text p-huge-bold">{{ model.name?.length > 40 ? (model.name | slice: 0 : 40) + '...' : model.name }}</div>
      <div class="d-flex flex-column flex-md-row justify-content-end align-items-center gap-half">
        @if (model.isin) {
          <web-components-watchlist-toggle [isin]="model.isin" iconSize="default" type="instrument-details-header" />
        }
        <web-design-system-icon-button
          icon="bell"
          type="grey-light"
          size="small"
          (click)="openPushAlertModal()"
          [ngbTooltip]="'alerts-toggle.tooltip.add' | transloco"
          [openDelay]="500"
          [closeDelay]="200"
        />
        <div class="d-none d-lg-flex gap-half">
          <ng-container *ngTemplateOutlet="tradingButtons; context: { fixedWidth: true }" />
        </div>
      </div>
    </div>
    <div class="d-none d-md-flex gap-half mb-3">
      @if (derivativeTopData?.length) {
        @for (field of derivativeTopData; track field.name) {
          <div class="w-fit b-r-4 p-2 zero-surface grey-800 zero-text p-xsmall-reg">
            <span class="zero-text p-xsmall-bold">{{ field.name }}: </span>
            {{ field.value ? field.value : '&mdash;' }}
          </div>
        }
      }
      <ng-container *ngTemplateOutlet="copyIsin" />
    </div>
    <div class="quote-row d-lg-flex justify-content-between">
      <div class="d-flex justify-content-between flex-fill">
        <ng-container
          *ngTemplateOutlet="quoteItem; context: { label: 'bid', quote: model.bid, perfAbs: model.perfBidAbs, perfPct: model.perfBidPct }"
        />
        <div class="d-flex">
          <web-design-system-divider class="d-none d-lg-block" orientation="vertical" />
          <ng-container
            *ngTemplateOutlet="quoteItem; context: { label: 'ask', quote: model.ask, perfAbs: model.perfAskAbs, perfPct: model.perfAskPct }"
          />
        </div>
      </div>
      <web-design-system-divider class="d-block d-lg-none" />
      <div class="d-flex justify-content-lg-end flex-fill">
        <div class="d-flex flex-column justify-content-between b-r-4 p-3 zero-surface grey-800 flex-fill flex-md-grow-0">
          <div class="quote-item d-flex flex-column justify-content-between">
            <div class="zero-text p-xsmall-bold">
              @if (underlying?.name && underlying?.isin && underlying?.isTradable) {
                {{ 'instrument-detail.instrument-header.quote-header.underlying' | transloco }}:
                <a class="link link-secondary zero-text p-xsmall-bold" [routerLink]="[instrumentDetail, underlying.isin]">{{
                  underlying.name
                }}</a>
              } @else {
                {{
                  ('instrument-detail.instrument-header.quote-header.underlying' | transloco) +
                    (underlying?.name ? ': ' + underlying?.name : '')
                }}
              }
            </div>
            @if (underlying?.quote) {
              <div class="d-flex align-items-center gap">
                <div class="zero-text h1-bold">
                  {{ underlying.quote | number: underlying.quoteFormat }}<sup>{{ underlying.currencyCode }}</sup>
                </div>

                <div class="d-block mt-1 mt-lg-0">
                  <div
                    class="zero-text p-xsmall-reg quoteindicator-variant3"
                    [class.course-green]="underlying.perfAbs >= 0"
                    [class.course-red]="underlying.perfAbs < 0"
                    zeroBlink
                    [val]="underlying.perfAbs"
                  >
                    @if (underlying.perfAbs > 0) {
                      +
                    }
                    {{ underlying.perfAbs | currency: underlying.currencyCode : 'symbol' : underlying.quoteFormat }}
                  </div>
                  <div
                    class="zero-text p-xsmall-reg quoteindicator-variant3"
                    [class.course-green]="underlying.perfPct >= 0"
                    [class.course-red]="underlying.perfPct < 0"
                    zeroBlink
                    [val]="underlying.perfPct"
                  >
                    @if (underlying.perfAbs > 0) {
                      +
                    }
                    {{ underlying.perfPct | number: '1.2-2' }} %
                  </div>
                </div>
              </div>
              <div class="zero-text p-xsmall-reg label">{{ underlying.quoteTime | todayTimeOrDate }}</div>
            } @else {
              <ng-container *ngTemplateOutlet="noValues" />
            }
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex d-lg-none mt-3 mt-lg-0 justify-content-between gap-half">
      <ng-container *ngTemplateOutlet="tradingButtons" />
    </div>
  </div>
</ng-template>

<ng-template #actionButtons>
  @if (model.isSavingsplanReady) {
    <web-design-system-icon-button
      data-zid="savings-plan-button"
      icon="pig"
      size="small"
      type="old-savings-plan"
      [disabled]="isPotentialCustomer"
      [routerLink]="savingsPlanForm + '/' + model.isin"
      [title]="'savings-plan.most-chosen.item.title' | transloco"
    />
  }
  <button
    class="trade buy ml-2 instrument-detail-header mb-3 font-bold"
    [routerLink]="tradeCreateForm"
    [queryParams]="{ isin: model.isin, direction: 'buy' }"
    [title]="'instrument-detail.instrument-header.buy' | transloco"
    [disabled]="!model.isBuyable || isPotentialCustomer"
  >
    {{ 'instrument-detail.instrument-header.buy' | transloco }}
    @if (model.ask) {
      <div class="quote">{{ model.ask | currency: model.currencyCode : 'symbol' : quoteFormat }}</div>
    }
  </button>
  <button
    class="trade sell ml-2 instrument-detail-header font-bold"
    [routerLink]="tradeCreateForm"
    [queryParams]="{ isin: model.isin, direction: 'sell', quantity: model.availableQuantity }"
    [title]="'instrument-detail.instrument-header.sell' | transloco"
    [disabled]="!model.hasAssetInPortfolio || !model.isSellable || isPotentialCustomer"
  >
    {{ 'instrument-detail.instrument-header.sell' | transloco }}
    @if (model.bid) {
      <div class="quote">
        {{ model.bid | currency: model.currencyCode : 'symbol' : quoteFormat }}
      </div>
    }
  </button>
  <div class="icon-container">
    <ng-container *ngTemplateOutlet="starAndBell" />
  </div>
</ng-template>

<ng-template #tradingButtons let-fixedWidth="fixedWidth">
  <web-design-system-icon-button
    class="button-full-width"
    size="small"
    type="green-light"
    [routerLink]="tradeCreateForm"
    [queryParams]="{ isin: model.isin, direction: 'buy' }"
    [disabled]="!model.isBuyable"
    [fullwidth]="true"
  >
    {{ 'instrument-detail.instrument-header.buy' | transloco | uppercase }}
  </web-design-system-icon-button>
  <web-design-system-icon-button
    class="button-full-width"
    size="small"
    type="red-light"
    [routerLink]="tradeCreateForm"
    [queryParams]="{ isin: model.isin, direction: 'sell', quantity: model.availableQuantity }"
    [title]="'instrument-detail.instrument-header.sell' | transloco"
    [disabled]="!model.hasAssetInPortfolio || !model.isSellable"
    [fullwidth]="true"
  >
    {{ 'instrument-detail.instrument-header.sell' | transloco | uppercase }}
  </web-design-system-icon-button>
</ng-template>

<ng-template #starAndBell>
  @if (model.isin) {
    <div class="watchlist-toggle">
      <web-components-watchlist-toggle [isin]="model.isin" iconSize="default" />
    </div>
  }
  <div
    class="cursor-pointer"
    (click)="openPushAlertModal()"
    [ngbTooltip]="'alerts-toggle.tooltip.add' | transloco"
    [openDelay]="500"
    [closeDelay]="200"
  >
    <svg class="icon icon-24">
      <use xlink:href="/assets/img/svgs/svgset.svg#bell-black" />
    </svg>
  </div>
</ng-template>

<ng-template #pushAlertModal let-modal>
  <instrument-details-push-alert-modal (handleClose)="modal.dismiss()" />
</ng-template>

<ng-template #copyIsin>
  <div class="w-fit b-r-4 px-2 py-1 zero-surface grey-800 zero-text p-xsmall-reg d-flex gap-small align-items-center">
    <div class="zero-text p-xsmall-bold">ISIN:</div>
    <web-design-system-copy-to-clipboard
      [label]="model.isin"
      [hint]="'instrument-detail.instrument-header.copy-isin' | transloco"
      iconCssClass="icon-secondary"
    />
  </div>
</ng-template>

<ng-template #quoteItem let-label="label" let-quote="quote" let-perfAbs="perfAbs" let-perfPct="perfPct">
  <div class="d-flex flex-column py-3 justify-content-between">
    <div class="zero-text p-xsmall-bold">{{ 'instrument-detail.instrument-header.quote-header.' + label | transloco }}</div>
    @if (quote) {
      <div class="d-lg-flex align-items-center gap">
        <div class="zero-text h1-bold">
          {{ quote | number: quoteFormat }}<sup>{{ model.currencySymbol }}</sup>
        </div>
        <div class="d-flex d-lg-block gap justify-content-between mt-1 mt-lg-0">
          <div
            class="zero-text p-xsmall-reg quoteindicator-variant3"
            [class.course-green]="perfAbs >= 0"
            [class.course-red]="perfAbs < 0"
            zeroBlink
            [val]="perfAbs"
          >
            @if (perfAbs > 0) {
              +
            }
            {{ perfAbs | currency: model.currencyCode : 'symbol' : quoteFormat }}
          </div>
          <div
            class="zero-text p-xsmall-reg quoteindicator-variant3"
            [class.course-green]="perfPct >= 0"
            [class.course-red]="perfPct < 0"
            zeroBlink
            [val]="perfPct"
          >
            @if (perfAbs > 0) {
              +
            }
            {{ perfPct | number: '1.2-2' }} %
          </div>
        </div>
      </div>
      <div class="zero-text p-xsmall-reg label mt-2 mt-lg-0">
        {{ model.quoteTime | todayTimeOrDate }}
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="noValues" />
    }
  </div>
</ng-template>

<ng-template #noValues>
  <div class="zero-text p-small-reg disabled">{{ 'instrument-detail.instrument-header.no-values' | transloco }}</div>
  <div></div>
</ng-template>
